import React from 'react';
import { Check, Music, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { useTranslation } from 'react-i18next';

const PricingSection = () => {
  const { t } = useTranslation();

  const scrollToContact = () => {
    const element = document.getElementById('kontakt');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const pricingTiers = [
    {
      name: t('pricing.tier1Name'),
      price: '5000 kr',
      description: t('pricing.tier1Desc'),
      features: [
        t('pricing.tier1Feature1'),
        t('pricing.tier1Feature2'),
        t('pricing.tier1Feature3'),
        t('pricing.tier1Feature4')
      ],
      highlighted: false
    },
    {
      name: t('pricing.tier2Name'),
      price: '8000 kr',
      description: t('pricing.tier2Desc'),
      features: [
        t('pricing.tier2Feature1'),
        t('pricing.tier2Feature2'),
        t('pricing.tier2Feature3'),
        t('pricing.tier2Feature4'),
        t('pricing.tier2Feature5')
      ],
      highlighted: true
    },
    {
      name: t('pricing.tier3Name'),
      price: t('pricing.tier3Price'),
      description: t('pricing.tier3Desc'),
      features: [
        t('pricing.tier3Feature1'),
        t('pricing.tier3Feature2'),
        t('pricing.tier3Feature3')
      ],
      highlighted: false,
      isCustom: true
    }
  ];

  return (
    <section id="pris" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <div className="inline-block px-4 py-2 bg-black text-white text-sm font-semibold rounded-full mb-4">
            {t('pricing.badge')}
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-black">
            {t('pricing.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('pricing.subtitle')}
          </p>
          <div className="w-20 h-1 bg-black mx-auto"></div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {pricingTiers.map((tier, index) => (
            <Card
              key={index}
              className={`relative transition-all duration-300 ${
                tier.highlighted
                  ? 'border-4 border-black shadow-2xl scale-105'
                  : 'border-2 border-gray-200 hover:border-black hover:shadow-xl'
              }`}
            >
              {tier.highlighted && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-black text-white px-4 py-1 rounded-full text-sm font-semibold">
                    {t('pricing.popular')}
                  </span>
                </div>
              )}
              
              <CardHeader className="text-center pb-8 pt-8">
                <div className="flex justify-center mb-4">
                  <div className={`p-4 rounded-full ${tier.highlighted ? 'bg-black' : 'bg-gray-100'}`}>
                    <Music className={`w-8 h-8 ${tier.highlighted ? 'text-white' : 'text-black'}`} />
                  </div>
                </div>
                <CardTitle className="text-2xl font-bold text-black mb-2">
                  {tier.name}
                </CardTitle>
                <CardDescription className="text-gray-600">
                  {tier.description}
                </CardDescription>
                <div className="mt-6">
                  <span className="text-5xl font-bold text-black">{tier.price}</span>
                  {!tier.isCustom && (
                    <span className="text-gray-500 text-sm ml-2">{t('pricing.perProject')}</span>
                  )}
                </div>
              </CardHeader>

              <CardContent className="space-y-4 pb-8">
                <ul className="space-y-3">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-3">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tier.highlighted ? 'text-black' : 'text-gray-600'}`} />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="pt-6">
                  <Button
                    onClick={scrollToContact}
                    className={`w-full transition-all duration-300 ${
                      tier.highlighted
                        ? 'bg-black text-white hover:bg-gray-800'
                        : 'bg-white text-black border-2 border-black hover:bg-black hover:text-white'
                    }`}
                  >
                    {tier.isCustom ? (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        {t('pricing.contactButton')}
                      </>
                    ) : (
                      t('pricing.orderButton')
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t('pricing.note')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
